import {
    IPlatforms,
    RoundEnded,
    RoundStarted,
    Cashier,
    History,
    Help,
    Paytable,
    Balance,
    SpinReceived,
    GameDataLoaded,
    FeatureData,
    AutoplayData,
    RoundInit,
    BetData
} from './IPlatforms';

import {PlatformIforium} from './PlatformIforium';
import {PlatformGan} from './PlatformGan';
import {PlatformGel} from './PlatformGel';
import {PlatformEveryMatrix} from './PlatformEveryMatrix';
import {PlatformRelax} from './PlatformRelax';
import {PlatformDummy} from './PlatformDummy';
import {PlatformPariplay} from './PlatformPariplay';
import {createEventEmmiter, IEventEmitter, Logger} from 'neo-common';
import {PlatformRelaxFEIM} from './PlatformRelaxFEIM';
import {PlatformBetFair} from './PlatformBetFair';
import {PlatformSynotAPI} from './PlatformSynotAPI';
import {PlatformPlaytech} from './PlatformPlaytech';
import {EventTracker, PlayerRetentionManager} from '../Plugins/index';
import {PlatformMicrogaming} from './PlatformMicrogaming';
import {TimeData} from '../Plugins/PlayerRetention/IClientEvents';

export class Platforms {

    static readonly AutoplayStop = 'autoplayStop';
    static readonly RestoreAutoplay = 'restoreAutoplay';
    static readonly ResizeGame = 'resizeGame';
    static readonly RefreshBalance = 'refreshBalance';
    static readonly GamePlayStop = 'gamePlayStop';
    static readonly GamePlayResume = 'gamePlayResume';
    static readonly GamePlayFreeze = 'gamePlayFreeze';
    static readonly GamePlayUnfreeze = 'gamePlayUnfreeze';
    static readonly GameAudioEnable = 'gameAudioEnable';
    static readonly GameAudioMute = 'gameAudioMute';
    static readonly GameAudioSet = 'gameAudioSet';
    static readonly GameQuickSpin = 'gameQuickSpin';
    static readonly ShowHelp = 'showHelp';
    static readonly HideHelp = 'hideHelp';
    static readonly ToggleHelp = 'toggleHelp';
    static readonly ShowPaytable = 'showPaytable';
    static readonly HidePaytable = 'hidePaytable';
    static readonly TogglePaytable = 'togglePaytable';
    static readonly PlatformErrorMessageDismissed = 'platformErrorMessageDismissed';
    static readonly PlatformCloseMenu = 'platformCloseMenu';
    static readonly PlatformCloseGame = 'platformCloseGame';
    static readonly PlatformUpdateUI = 'platformUpdateUI';
    static readonly CreateNotification = 'createNotification';

    static externalWrappersWidth: number = 0;
    static externalWrappersHeight: number = 0;

    static canPlay: boolean = true;

    //handle whenever spin is allowed after collect if autoplay is enabled
    static canSpinOnCollect: boolean = true;

    //operator handle opening error dialog itself or not
    static handleShowErrorDialog: boolean = false;

    //operator handle opening cashier page itself or not
    static handleOpenCashierPage: boolean = false;

    //operator handle opening history page itself or not
    static handleOpenHistoryPage: boolean = false;

    //operator handle game exit itself or not
    static handleGameExit: boolean = false;

    //operator handle game reload itself or not
    static handleGameReload: boolean = false;

    //operator handle opening another game from similar lobby itself or not
    static handleChangeGame: boolean = false;

    //store info about exit handler
    static exitHandler: string = '';

    //operator handle game fullscreen itself or not
    static handleFullscreen: boolean = false;

    static platform: IPlatforms;
    static pluginUrl: string;
    static pluginOrigin: string;
    static gameOrigin: string;
    static gameKey: string;
    static lang: string;

    static roundStartedFlag: boolean = true;

    static eventTracker: EventTracker;

    static eventEmitter: IEventEmitter;

    static playerRetentionManager: PlayerRetentionManager;

    static getTimeDataFnc: () => TimeData;

    static getGameTimeFnc: (utcTime: string) => string;

    static createEmitter(): void {
        Platforms.eventEmitter = createEventEmmiter();
    }

    /**********************************
     * GAME -> PLATFORM
     *********************************/

    /**
     * Create a listener to receive messages from platform.
     * @param platform
     * @param pluginUrl
     * @param pluginOrigin
     * @param additionalData
     */
    static create(platform: string, pluginUrl: string, pluginOrigin: string, additionalData?: {gameKey: string, lang: string, clientHub: any}): void {
        Platforms.pluginUrl = pluginUrl;
        Platforms.pluginOrigin = Platforms.getUrlOrigin(pluginOrigin);
        Platforms.gameOrigin = window.location.origin;

        if (additionalData != null) {
            Platforms.gameKey = additionalData.gameKey;
            Platforms.lang = additionalData.lang;
        }

        switch (platform) {
            case 'iforium':
                Platforms.platform = new PlatformIforium();
                break;
            case 'gan':
                Platforms.platform = new PlatformGan();
                break;
            case 'gel':
                Platforms.platform = new PlatformGel();
                break;
            case 'everymatrix':
                Platforms.platform = new PlatformEveryMatrix();
                break;
            case 'betfair':
                Platforms.platform = new PlatformBetFair();
                break;
            case 'relax':
                Platforms.platform = new PlatformRelax();
                break;
            case 'relaxFEIM':
                Platforms.platform = new PlatformRelaxFEIM();
                break;
            case 'dummy':
                Platforms.platform = new PlatformDummy();
                break;
            case 'pariplay':
                Platforms.platform = new PlatformPariplay();
                break;
            case 'synotAPI':
                Platforms.platform = new PlatformSynotAPI();
                break;
            case 'playtech':
                Platforms.platform = new PlatformPlaytech();
                break;
            case 'microgaming':
                Platforms.platform = new PlatformMicrogaming();
                break;
            default:
                Platforms.platform = null;
                break;
        }

        Platforms.eventTracker = new EventTracker();
        Platforms.eventTracker.on(EventTracker.EVENTS_TRACKER, (data: any) => {Platforms.createNotification(data)});

        Platforms.playerRetentionManager = new PlayerRetentionManager(additionalData.clientHub);

        if (Platforms.platform != null) {
            Platforms.platform.create();
        }
    }

    static getUrlOrigin(url: string): string {
        if (url == null || url === '' || url === '*') {
            return '*';
        }

        try {
            const uri = new URL(url);
            return uri.origin;
        } catch (e) {
            if (DEBUG) {
                Logger.debugGroup('Platforms', "Provided url for getting origin is not valid, returning '*'.");
            }
        }

        return '*'
    }

    /**
     * Post game load progress data to the parent.
     * @param progress
     */
    static loadProgress(progress: number): void {
        if (Platforms.platform != null) {
            Platforms.platform.loadProgress(progress);
        }

        if (Platforms.playerRetentionManager != null) {
            Platforms.playerRetentionManager.loadProgress(progress);
        }
    }

    /**
     * Post info to the parent that game loaded successfully.
     */
    static gameReady(): void {
        if (Platforms.platform != null) {
            Platforms.platform.gameReady();
        }

        if (Platforms.eventTracker != null) {
            Platforms.eventTracker.gameReady();
        }

        if (Platforms.playerRetentionManager != null) {
            Platforms.playerRetentionManager.gameReady();
        }
    }

    /**
     * Post info to the parent that game load failed.
     */
    static gameFailed(): void {
        if (Platforms.platform != null) {
            Platforms.platform.gameFailed();
        }
    }

    /**
     * Post info to the parent on game exit.
     */
    static gameClosing(): void {
        if (Platforms.platform != null) {
            Platforms.platform.gameClosing();
        }

        if (Platforms.eventTracker != null) {
            Platforms.eventTracker.gameClosing(Platforms.exitHandler);
        }
    }

    /**
     * Post info to the parent on game reload.
     */
    static gameReloading(): void {
        if (Platforms.platform != null) {
            Platforms.platform.gameReloading();
        }
    }

    /**
     * Post info to the parent on game exit.
     */
    static gameChange(gameCode: string): void {
        if (Platforms.platform != null) {
            Platforms.platform.gameChange(gameCode);
        }
    }

    /**
     * Post info to the parent when error received.
     * @param code
     * @param message
     */
    static error(code: string, message: string): void {
        if (Platforms.platform != null) {
            Platforms.platform.error(code, message);
        }

        if (Platforms.playerRetentionManager != null) {
            Platforms.playerRetentionManager.error(code, message);
        }
    }

    /**
     * Post info to the parent when client show error (not created by request).
     * e.g. not_enough_credit
     * @param id
     * @param message
     */
    static clientError(id: string, message: string): void {
        if (Platforms.platform != null) {
            Platforms.platform.clientError(id, message);
        }
    }

    /**
     * Post info to the parent when spin is prepared.
     * @param args
     */
    static roundInit(args?: RoundInit): void {
        if (Platforms.platform != null) {
            Platforms.platform.roundInit(args);
        }

        if (Platforms.playerRetentionManager != null) {
            Platforms.playerRetentionManager.roundInit();
        }
    }

    /**
     * Post info to the parent when spin action is emitted.
     * @param args
     */
    static roundStarted(args?: RoundStarted): void {
        if (Platforms.platform != null) {
            Platforms.platform.roundStarted(args);
            Platforms.roundStartedFlag = true;
        }

        if (Platforms.eventTracker != null) {
            Platforms.eventTracker.roundStarted(args);
        }

        if (Platforms.playerRetentionManager != null) {
            Platforms.playerRetentionManager.roundStarted();
        }
    }

    /**
     * Post info to the parent when spin action is emitted.
     */
    static spinStarted(): void {
        if (Platforms.platform != null) {
            Platforms.platform.spinStarted();
        }

        if (Platforms.playerRetentionManager != null) {
            Platforms.playerRetentionManager.spinStarted();
        }
    }

    /**
     * Post info to the parent when spin response is received.
     */
    static spinReceived(args?: SpinReceived): void {
        if (Platforms.platform != null) {
            Platforms.platform.spinReceived(args);
        }

        if (Platforms.playerRetentionManager != null) {
            Platforms.playerRetentionManager.spinReceived(args);
        }
    }

    /**
     * Post info to the parent when machine stopped.
     */
    static spinEnded(): void {
        if (Platforms.platform != null) {
            Platforms.platform.spinEnded();
        }

        if (Platforms.playerRetentionManager != null) {
            Platforms.playerRetentionManager.spinEnded();
        }
    }

    /**
     * Post info to the parent on collect received;
     * on spin action when there was win and the game round isn’t closed;
     * on autoplay stop;
     * when machine stopped and there wasn’t any win and the game round is closed;
     * on win calculation finished when the game round is closed;
     * on intro finished when the game round is closed.
     * @param args
     */
    static roundEnded(args?: RoundEnded): void {
        if (Platforms.platform != null && Platforms.roundStartedFlag) {
            Platforms.platform.roundEnded(args);
            Platforms.roundStartedFlag = false;
        }

        if (Platforms.eventTracker != null) {
            Platforms.eventTracker.roundEnded(args);
        }

        if (Platforms.playerRetentionManager != null) {
            Platforms.playerRetentionManager.roundEnded();
        }
    }

    /**
     * Post info to the parent when feature game started.
     */
    static featureStarted(args?: FeatureData): void {
        if (Platforms.platform != null) {
            Platforms.platform.featureStarted(args);
        }

        if (Platforms.playerRetentionManager != null) {
            Platforms.playerRetentionManager.featureStarted();
        }
    }

    /**
     * Post info to the parent if game is in feature after loading.
     */
    static featureInit(args?: FeatureData): void {
        if (Platforms.platform != null) {
            Platforms.platform.featureInit(args);
        }

        if (Platforms.playerRetentionManager != null) {
            Platforms.playerRetentionManager.featureInit();
        }
    }

    /**
     * Post info to the parent when feature game finished.
     */
    static featureFinished(args?: FeatureData): void {
        if (Platforms.platform != null) {
            Platforms.platform.featureFinished(args);
        }

        if (Platforms.playerRetentionManager != null) {
            Platforms.playerRetentionManager.featureFinished();
        }
    }

    /**
     * Post info to the parent when autoplay started.
     */
    static autoplayStarted(): void {
        if (Platforms.platform != null) {
            Platforms.platform.autoplayStarted();
        }

        if (Platforms.eventTracker != null) {
            Platforms.eventTracker.autoplay(true);
        }

        if (Platforms.playerRetentionManager != null) {
            Platforms.playerRetentionManager.autoplayStarted();
        }
    }

    /**
     * Post info to the parent when autoplay finished.
     */
    static autoplayFinished(args?: AutoplayData): void {
        if (Platforms.platform != null) {
            Platforms.platform.autoplayFinished(args);
        }

        if (Platforms.eventTracker != null) {
            Platforms.eventTracker.autoplay(false);
        }

        if (Platforms.playerRetentionManager != null) {
            Platforms.playerRetentionManager.autoplayFinished(args);
        }
    }

    /**
     * Post info to the parent when autoplay finished.
     */
    static autoplayStop(): void {
        /*if (Platforms.platform != null) {
            Platforms.platform.autoplayStop();
        }*/

        if (Platforms.eventTracker != null) {
            Platforms.eventTracker.autoplayStop();
        }
    }

    /**
     * Post info to the parent when click on the “cashier button” was performed.
     * @param args
     */
    static gotoCasier(args?: Cashier): void {
        if (Platforms.platform != null) {
            Platforms.platform.gotoCasier(args);
        }
    }

    /**
     * Post info to the parent when “open game round history” action is emitted.
     * @param args
     */
    static gotoHistory(args?: History): void {
        if (Platforms.platform != null) {
            Platforms.platform.gotoHistory(args);
        }

        if (Platforms.eventTracker != null) {
            Platforms.eventTracker.gotoHistory(args);
        }
    }

    /**
     * Post info to the parent when “open help” action is emitted.
     * @param args
     */
    static gotoHelp(args?: Help): void {
        if (Platforms.platform != null) {
            Platforms.platform.gotoHelp(args);
        }

        if (Platforms.eventTracker != null) {
            Platforms.eventTracker.gotoHelp(args);
        }

        if (Platforms.playerRetentionManager != null) {
            Platforms.playerRetentionManager.gotoHelp();
        }
    }

    /**
     * Post info to the parent when “open paytable” action is emitted.
     * @param args
     */
    static gotoPaytable(args?: Paytable): void {
        if (Platforms.platform != null) {
            Platforms.platform.gotoPaytable(args);
        }

        if (Platforms.eventTracker != null) {
            Platforms.eventTracker.gotoPaytable(args);
        }

        if (Platforms.playerRetentionManager != null) {
            Platforms.playerRetentionManager.gotoPaytable();
        }
    }

    static gotoSettings(): void {
        /*if (Platforms.platform != null) {
            Platforms.platform.gotoSettings();
        }*/

        if (Platforms.eventTracker != null) {
            Platforms.eventTracker.gotoSettings();
        }

        if (Platforms.playerRetentionManager != null) {
            Platforms.playerRetentionManager.gotoSettings();
        }
    }

    /**
     *
     * @param args
     */
    static balanceUpdated(args?: Balance): void {
        if (Platforms.platform != null) {
            Platforms.platform.balanceUpdated(args);
        }
    }

    /**
     * Post custom message to parent when message type is “plugin”.
     * @param message
     */
    static sendPluginMessage(message: string): void {
        if (Platforms.platform != null) {
            Platforms.platform.sendPluginMessage(message);
        }
    }

    /**
     * Post info to the parent when “toggle sound” action is emitted.
     * @param level
     */
    static setVolume(level: number): void {
        if (Platforms.platform != null) {
            Platforms.platform.setVolume(level);
        }

        if (Platforms.eventTracker != null) {
            Platforms.eventTracker.setVolume(level);
        }

        if (Platforms.playerRetentionManager != null) {
            Platforms.playerRetentionManager.setVolume(level);
        }
    }

    /**
     * Post info to the parent when “quickspin” settings is changed.
     * @param enabled
     */
    static quickSpinEnabled(enabled: boolean): void {
        if (Platforms.platform != null) {
            Platforms.platform.quickSpinEnabled(enabled);
        }

        if (Platforms.eventTracker != null) {
            Platforms.eventTracker.quickSpinEnabled(enabled);
        }
    }

    /**
     * Post info to the parent when “quickspin” settings is changed.
     * @param enabled
     */
    static quickSpinDialog(result: boolean): void {
        /*if (Platforms.platform != null) {
            Platforms.platform.quickSpinDialog(result);
        }*/

        if (Platforms.eventTracker != null) {
            Platforms.eventTracker.quickSpinDialog(result);
        }
    }

    /**
     * Post info to the parent when bet was changed.
     * @param betData
     */
    static bet(betData: BetData): void {
        if (Platforms.platform != null) {
            Platforms.platform.bet(betData.totalBet);
        }

        if (Platforms.eventTracker != null) {
            Platforms.eventTracker.bet(betData);
        }

        if (Platforms.playerRetentionManager != null) {
            Platforms.playerRetentionManager.betChanged(betData);
        }
    }

    /**
     * Post info to the parent when win calculation finished.
     * @param value
     */
    static win(value: number, cumulative?: boolean): void {
        if (Platforms.platform != null) {
            Platforms.platform.win(value, cumulative);
        }
    }

    /**
     * Post exit url to the parent on game exit when “exit url” is defined.
     * @param action
     * @param data
     */
    static userAction(action: string, data: Object): void {
        if (Platforms.platform != null) {
            Platforms.platform.userAction(action, data);
        }
    }

    /**
     * Post info to the parent when game data loaded.
     * @param args
     */
    static gameDataLoaded(args: GameDataLoaded): void {
        if (Platforms.platform != null) {
            Platforms.platform.gameDataLoaded(args);
        }

        if (Platforms.playerRetentionManager != null) {
            Platforms.playerRetentionManager.gameStarted(args);
        }
    }

    /**
     * Post info to the parent when game opened any dialog and user input is required.
     * @param type - type of opened dialog (default dialog, cashier dialog, responsible dialog ...)
     * @param isOpen - information about state of current dialog
     */
    static dialogOpened(type: string, isOpen: boolean): void {
        if (Platforms.platform != null) {
            Platforms.platform.dialogOpened(type, isOpen);
        }

        if (Platforms.playerRetentionManager != null) {
            Platforms.playerRetentionManager.dialogOpened(type, isOpen);
        }
    }

    static fullscreen(enabled: boolean): void {
        if (Platforms.platform != null) {
            Platforms.platform.fullscreen(enabled)
        }
    }

    static gameCloseMenu(): void {
        if (Platforms.playerRetentionManager != null) {
            Platforms.playerRetentionManager.closeMenu();
        }
    }

    /**
     * Post info about renderer rype of canvas element.
     * @param rendererType - type of renderer: webGL, canvas ...
     */
    static rendererType(rendererType: string): void {
        /*if (Platforms.platform != null) {
            Platforms.platform.rendererType(rendererType);
        }*/

        if (Platforms.eventTracker != null) {
            Platforms.eventTracker.rendererType(rendererType);
        }
    }

    static webSocketsInitialized(): void {
        if (Platforms.playerRetentionManager != null) {
            Platforms.playerRetentionManager.webSocketsInitialized();
        }
    }

    static iconsCountAvailable(count: number): void {
        if (Platforms.playerRetentionManager != null) {
            Platforms.playerRetentionManager.iconsCountAvailable(count);
        }
    }

    /**
     * Post info about player's clock (de)synchronization with server time.
     * @param syncDiff - difference between server time and player time in ms
     */
    static clockSync(syncDiff: number): void {
        if (Platforms.eventTracker != null) {
            Platforms.eventTracker.clockSync(syncDiff);
        }
    }

    /**********************************
     * PLATFORM -> GAME
     *********************************/

    /**
     * Stop autoplay.
     */
    static stopAutoplay(): void {
        Platforms.eventEmitter.emit(Platforms.AutoplayStop);
    }

    static restoreAutoplay(args?: AutoplayData): void {
        Platforms.eventEmitter.emit(Platforms.RestoreAutoplay, args);
    }

    /**
     * Resize game.
     */
    static resizeGame(): void {
        Platforms.eventEmitter.emit(Platforms.ResizeGame);
    }

    /**
     * Refresh balance.
     */
    static refreshBalance(): void {
        Platforms.eventEmitter.emit(Platforms.RefreshBalance);
    }

    /**
     * Stop autoplay. There can by any other functionality in the future.
     */
    static gamePlayStop(disableChannel = false): void {
        Platforms.eventEmitter.emit(Platforms.GamePlayStop, disableChannel);

        if (Platforms.playerRetentionManager != null) {
            Platforms.playerRetentionManager.gamePlayStop();
        }
    }

    /**
     * Resume game.
     */
    static gamePlayResume(): void {
        Platforms.canSpinOnCollect = true;
        Platforms.eventEmitter.emit(Platforms.GamePlayResume);

        if (Platforms.playerRetentionManager != null) {
            Platforms.playerRetentionManager.gamePlayResume();
        }
    }

    /**
     * Freeze game. Animations, tweens, sounds included.
     */
    static gamePlayFreeze(): void {
        Platforms.eventEmitter.emit(Platforms.GamePlayFreeze);

        if (Platforms.playerRetentionManager != null) {
            Platforms.playerRetentionManager.gamePlayFreeze();
        }
    }

    /**
     * Unfreeze game. Animations, tweens, sounds included.
     */
    static gamePlayUnfreeze(): void {
        Platforms.canSpinOnCollect = true;
        Platforms.eventEmitter.emit(Platforms.GamePlayUnfreeze);

        if (Platforms.playerRetentionManager != null) {
            Platforms.playerRetentionManager.gamePlayUnfreeze();
        }
    }

    /**
     * Supress autocollected spin.
     */
    static gamePlaySupressSpinOnCollect(): void {
        Platforms.canSpinOnCollect = false;
    }

    /**
     * Set sound volume to level 3. Enable audio.
     */
    static gameAudioEnable(): void {
        Platforms.eventEmitter.emit(Platforms.GameAudioEnable);
    }

    /**
     * Set sound volume to level 0. Mute audio.
     */
    static gameAudioMute(): void {
        Platforms.eventEmitter.emit(Platforms.GameAudioMute);
    }

    /**
     * Set sound volume to level 0. Mute audio.
     */
    static gameAudioSet(level: number): void {
        Platforms.eventEmitter.emit(Platforms.GameAudioSet, level);
    }

    /**
     * Set game quick spin enable.
     */
    static gameQuickSpin(enable: boolean): void {
        Platforms.eventEmitter.emit(Platforms.GameQuickSpin, enable);
    }

    /**
     * Show help.
     */
    static showHelp(): void {
        Platforms.eventEmitter.emit(Platforms.ShowHelp);
    }

    /**
     * Hide help.
     */
    static hideHelp(): void {
        Platforms.eventEmitter.emit(Platforms.HideHelp);
    }

    /**
     * Toggle help.
     */
    static toggleHelp(): void {
        Platforms.eventEmitter.emit(Platforms.ToggleHelp);
    }

    /**
     * Show paytable.
     */
    static showPaytable(): void {
        Platforms.eventEmitter.emit(Platforms.ShowPaytable);
    }

    /**
     * Hide paytable.
     */
    static hidePaytable(): void {
        Platforms.eventEmitter.emit(Platforms.HidePaytable);
    }

    /**
     * Show/hide paytable.
     */
    static togglePaytable(): void {
        Platforms.eventEmitter.emit(Platforms.TogglePaytable);
    }

    /**
     * Error dialog in operator window was closed.
     */
    static platformErrorMessageDismissed(): void {
        Platforms.eventEmitter.emit(Platforms.PlatformErrorMessageDismissed);
    }

    /**
     * Close game
     */
    static closeMenu(): void {
        Platforms.eventEmitter.emit(Platforms.PlatformCloseMenu);
    }

    /**
     * Close game
     */
    static closeGame(): void {
        Platforms.eventEmitter.emit(Platforms.PlatformCloseGame);
    }

    static updateUI(): void {
        Platforms.eventEmitter.emit(Platforms.PlatformUpdateUI);
    }

    static createNotification(data: any): void {
        Platforms.eventEmitter.emit(Platforms.CreateNotification, data);
    }

    static getTimeData(): TimeData {
        if (Platforms.getTimeDataFnc != null) {
            return Platforms.getTimeDataFnc();
        }

        return null;
    }

    static getGameTime(utcTime: string): string {
        if (Platforms.getGameTimeFnc != null) {
            return Platforms.getGameTimeFnc(utcTime);
        }

        return null;
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Platforms/Platforms.ts
 **/